import { createTheme, ThemeOptions } from '@mui/material/styles';

// SimplyPayroll Brand Colors (Updated Dec 2025)
const brandColors = {
  primary: {
    main: '#1E23FE',
    light: '#5C60FE',
    dark: '#1B35E9',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#0D9E6F',
    light: '#0CA963',
    dark: '#087A54',
    contrastText: '#ffffff',
  },
  success: {
    main: '#0CA963',
    light: '#10C977',
    dark: '#087A54',
  },
  error: {
    main: '#D32F2F',
    light: '#EF5350',
    dark: '#C62828',
  },
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
  },
  info: {
    main: '#1374A1',
    light: '#1A8FC4',
    dark: '#0E5A7D',
  },
};

const getDesignTokens = (mode: 'light' | 'dark'): ThemeOptions => ({
  palette: {
    mode,
    primary: brandColors.primary,
    secondary: brandColors.secondary,
    success: brandColors.success,
    error: brandColors.error,
    warning: brandColors.warning,
    info: brandColors.info,
    background: {
      default: mode === 'light' ? '#ffffff' : '#121212',
      paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
    },
    text: {
      primary: mode === 'light' ? '#1a1a1a' : '#f5f5f5',
      secondary: mode === 'light' ? '#555555' : '#b0b0b0',
    },
    divider:
      mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '3.5rem',
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.6,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none' as const,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'light' ? '#c1c1c1' : '#555',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: mode === 'light' ? '#a8a8a8' : '#777',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 24px',
          fontSize: '1rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 8px rgba(76, 175, 80, 0.25)',
          },
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '1.0625rem',
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(30, 35, 254, 0.3)',
          },
        },
        containedPrimary: {
          background: '#1E23FE',
          '&:hover': {
            background: '#1B35E9',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow:
            mode === 'light'
              ? '0px 4px 20px rgba(0, 0, 0, 0.08)'
              : '0px 4px 20px rgba(0, 0, 0, 0.3)',
          border: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.05)' : 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brandColors.primary.main,
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
  },
});

export const createAppTheme = (mode: 'light' | 'dark' = 'light') =>
  createTheme(getDesignTokens(mode));

export default createAppTheme;
